import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "@store/baseQuery";
import { parseJsonOrEmpty } from "@utils/parseJsonResponse";
import type {
    Connection,
    Pipeline,
    CreateConnectionRequest,
    UpdateConnectionRequest,
    CreatePipelineRequest,
    UpdatePipelineRequest,
    DatabricksAuthRequest,
    DatabricksAuthResponse,
    DatabricksCallbackParams,
    DatabricksCallbackResponse,
} from "@utils/types/integration";

export interface DeactivationImpactStack {
    id: number;
    name: string;
}

export interface ConnectionDeactivationImpact {
    connection_id?: number;
    connection_name?: string;
    connection_status?: string;
    active_stacks?: DeactivationImpactStack[];
    requires_confirmation?: boolean;
    [key: string]: unknown;
}

export interface DatabricksLakebaseOption {
    id: string;
    name: string;
}

export const integrationApi = createApi({
    reducerPath: "integrationApi",
    baseQuery: customBaseQuery,
    tagTypes: ["Connections", "Pipelines"],
    endpoints: (builder) => ({
        /* =========================
         * Connections
         * ========================= */

        getConnectionsList: builder.query<Connection[], void>({
            query: () => ({
                url: "/integrations/connections/",
                method: "GET",
            }),
            providesTags: ["Connections"],
        }),

        getConnectionById: builder.query<Connection, number>({
            query: (id) => ({
                url: `/integrations/connections/${id}/`,
                method: "GET",
            }),
            providesTags: (_r, _e, id) => [{ type: "Connections", id }],
        }),

        createConnection: builder.mutation<Connection, CreateConnectionRequest>({
            query: (payload) => ({
                url: "/integrations/connections/",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["Connections", "Pipelines"],
        }),

        updateConnection: builder.mutation<
            Connection,
            { id: number; data: UpdateConnectionRequest }
        >({
            query: ({ id, data }) => ({
                url: `/integrations/connections/${id}/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Connections", "Pipelines"],
        }),

        deleteConnection: builder.mutation<void, number>({
            query: (id) => ({
                url: `/integrations/connections/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["Connections", "Pipelines"],
        }),

        getConnectionDeactivationImpact: builder.query<ConnectionDeactivationImpact, number>({
            query: (id) => ({
                url: `/integrations/connections/${id}/deactivation-impact/`,
                method: "GET",
            }),
        }),

        databricksAuth: builder.mutation<DatabricksAuthResponse, DatabricksAuthRequest>({
            query: (payload) => ({
                url: "/integrations/connections/databricks-auth/",
                method: "POST",
                body: payload,
                responseHandler: parseJsonOrEmpty,
            }),
        }),

        databricksCallback: builder.query<
            DatabricksCallbackResponse,
            DatabricksCallbackParams
        >({
            query: ({ code, state }) => ({
                url: "/integrations/connections/databricks-callback/",
                method: "GET",
                params: { code, state },
                responseHandler: parseJsonOrEmpty,
            }),
        }),
        getDatabricksMetadata: builder.query<DatabricksCallbackResponse, number>({
            query: (id) => ({
                url: `/integrations/connections/${id}/databricks/metadata/`,
                method: "GET",
                responseHandler: parseJsonOrEmpty,
            }),
        }),
        getDatabricksLakebaseOptions: builder.query<
            unknown,
            {
                connectionId: number;
                type: string;
                project?: string;
                database_name?: string;
                branch?: string;
            }
        >({
            query: ({ connectionId, type, project, database_name, branch }) => ({
                url: `/integrations/connections/${connectionId}/databricks-lakebase-options/`,
                method: "GET",
                params: {
                    type,
                    ...(project ? { project } : {}),
                    ...(database_name ? { database_name } : {}),
                    ...(branch ? { branch } : {}),
                },
                responseHandler: parseJsonOrEmpty,
            }),
        }),

        getTsOrganizations: builder.query<
            { id: string | number; name: string }[] | { organizations: { id: string | number; name: string }[] },
            { cluster_url: string; username: string; secret_key: string }
        >({
            query: ({ cluster_url, username, secret_key }) => ({
                url: "/settings/ts-organizations",
                method: "GET",
                params: {
                    cluster_url,
                    username,
                    secret_key,
                },
            }),
        }),

        /* =========================
         * Pipelines
         * ========================= */

        getPipelinesList: builder.query<Pipeline[], void>({
            query: () => ({
                url: "/integrations/pipelines/",
                method: "GET",
            }),
            providesTags: ["Pipelines"],
        }),

        getPipelineById: builder.query<Pipeline, number>({
            query: (id) => ({
                url: `/integrations/pipelines/${id}/`,
                method: "GET",
            }),
            providesTags: (_r, _e, id) => [{ type: "Pipelines", id }],
        }),

        createPipeline: builder.mutation<Pipeline, CreatePipelineRequest>({
            query: (payload) => ({
                url: "/integrations/pipelines/",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["Pipelines"],
        }),

        updatePipeline: builder.mutation<
            Pipeline,
            { id: number; payload: UpdatePipelineRequest }
        >({
            query: ({ id, payload }) => ({
                url: `/integrations/pipelines/${id}/`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: ["Pipelines"],
        }),

        deletePipeline: builder.mutation<void, number>({
            query: (id) => ({
                url: `/integrations/pipelines/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["Pipelines"],
        }),
    }),
});

export const {
    // Connections
    useGetConnectionsListQuery,
    useGetConnectionByIdQuery,
    useCreateConnectionMutation,
    useUpdateConnectionMutation,
    useDeleteConnectionMutation,
    useLazyGetConnectionDeactivationImpactQuery,
    useDatabricksAuthMutation,
    useLazyDatabricksCallbackQuery,
    useLazyGetDatabricksLakebaseOptionsQuery,
    useLazyGetDatabricksMetadataQuery,
    useLazyGetTsOrganizationsQuery,

    // Pipelines
    useGetPipelinesListQuery,
    useGetPipelineByIdQuery,
    useCreatePipelineMutation,
    useUpdatePipelineMutation,
    useDeletePipelineMutation,
} = integrationApi;